/**
 * Cuentas del historial PIVE, como en la hoja del laboratorio: por donante,
 * TOTAL (sumas) y PROMEDIO (porcentajes sobre esas sumas).
 */

export interface NumerosFila {
  viables: number;
  totales: number;
  cultivados: number | null;
  clivados: number | null;
  embriones: number | null;
}

export interface ResumenHistorial {
  opus: number;
  viables: number;
  totales: number;
  cultivados: number;
  clivados: number;
  embriones: number;
  /** Viables sobre ovocitos totales. */
  pctViables: number | null;
  /** Cultivados sobre ovocitos totales. */
  pctCultivados: number | null;
  /** Clivados sobre cultivados. */
  pctClivados: number | null;
  /** Embriones sobre cultivados: el % de producción. */
  pctProduccion: number | null;
}

export function porcentaje(parte: number | null, total: number | null): number | null {
  return parte !== null && total ? parte / total * 100 : null;
}

export function resumenHistorial(filas: NumerosFila[]): ResumenHistorial {
  const suma = (valor: (f: NumerosFila) => number | null) => filas.reduce((total, f) => total + (valor(f) ?? 0), 0);
  // El % de producción solo cuenta los OPU con cultivados y embriones cargados,
  // para que un OPU en curso no baje el promedio.
  const cerradas = filas.filter(f => f.cultivados && f.embriones !== null);
  const resumen = {
    opus: filas.length,
    viables: suma(f => f.viables),
    totales: suma(f => f.totales),
    cultivados: suma(f => f.cultivados),
    clivados: suma(f => f.clivados),
    embriones: suma(f => f.embriones),
  };

  return {
    ...resumen,
    pctViables: porcentaje(resumen.viables, resumen.totales),
    pctCultivados: porcentaje(resumen.cultivados, resumen.totales),
    pctClivados: porcentaje(resumen.clivados, resumen.cultivados),
    pctProduccion: porcentaje(
      cerradas.reduce((t, f) => t + (f.embriones ?? 0), 0),
      cerradas.reduce((t, f) => t + (f.cultivados ?? 0), 0),
    ),
  };
}

/** Agrupa conservando el orden de llegada dentro de cada grupo. */
export function agruparPor<T, K>(items: T[], clave: (item: T) => K): Map<K, T[]> {
  const grupos = new Map<K, T[]>();
  for (const item of items) {
    const k = clave(item);
    grupos.set(k, [...(grupos.get(k) ?? []), item]);
  }
  return grupos;
}
